import { Box } from '@mui/material'
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp'
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined'
import EmojiEvents from '@mui/icons-material/EmojiEvents'
import MilitaryTech from '@mui/icons-material/MilitaryTech'
import mlIcon from '../../assets/icons/ML.svg'
import Tooltip from '../../shared/Tooltip/Tooltip'
import { hasScoreOrRank, selectPathPreview } from './drSecDimConfig'
import DrSecDimPathPreview from './DrSecDimPathPreview'
import DrSecDimRecommendation from './DrSecDimRecommendation'
import * as Styled from './DrSecDimStyles'

interface DrSecDimCardProps {
	mentor: MentorSnapshot
	onClose: () => void
	selectedNodeIds: Set<string>
	onToggleNode: (id: string) => void
	selectedCount: number
	isRegenerateBusy: boolean
	canRegenerate: boolean
	onRegenerateControlClick: () => void
	onRestartTutorial: () => void
}

function DrSecDimCard({
	mentor,
	onClose,
	selectedNodeIds,
	onToggleNode,
	selectedCount,
	isRegenerateBusy,
	canRegenerate,
	onRegenerateControlClick,
	onRestartTutorial,
}: DrSecDimCardProps) {
	const previewNodes = selectPathPreview(mentor.path)

	return (
		<Styled.ContentWrapper data-tour='dr-secdim-panel-content'>
			<Styled.PanelBody>
				<Styled.HeaderRow>
					<Styled.MentorIdentity>
						<Styled.MentorAvatar src={mlIcon} alt='' />
						<Box>
							<Styled.MentorName variant='h5'>{mentor.name}</Styled.MentorName>
							<Styled.MentorTitle variant='caption'>{mentor.title}</Styled.MentorTitle>
						</Box>
					</Styled.MentorIdentity>
					<Styled.HeaderActions>
						{hasScoreOrRank(mentor) && (
							<Styled.ScoreRankRow>
								{mentor.score !== undefined && (
									<Styled.StatPill>
										<EmojiEvents />
										{mentor.score.toLocaleString()} pts
									</Styled.StatPill>
								)}
								{mentor.rank && (
									<Styled.StatPill>
										<MilitaryTech />
										{mentor.rank}
									</Styled.StatPill>
								)}
							</Styled.ScoreRankRow>
						)}
						<Tooltip title='Replay tutorial'>
							<Styled.PanelCloseButton
								type='button'
								aria-label='Replay Dr. SecDim tutorial'
								onClick={onRestartTutorial}
							>
								<HelpOutlineIcon fontSize='inherit' />
							</Styled.PanelCloseButton>
						</Tooltip>
					</Styled.HeaderActions>
					<Styled.HeaderCloseAnchor>
						<Styled.PanelCloseButton
							type='button'
							data-tour='dr-secdim-close'
							aria-label='Close Dr. SecDim'
							onClick={onClose}
						>
							<KeyboardDoubleArrowUpIcon fontSize='inherit' />
						</Styled.PanelCloseButton>
					</Styled.HeaderCloseAnchor>
				</Styled.HeaderRow>
				{mentor.overallJustification && (
					<Styled.OverallJustification>
						{mentor.overallJustification}
					</Styled.OverallJustification>
				)}
				{previewNodes.length > 0 ? (
					<DrSecDimPathPreview
						nodes={previewNodes}
						selectedNodeIds={selectedNodeIds}
						onToggleNode={onToggleNode}
						disabled={isRegenerateBusy}
					/>
				) : (
					<Styled.StateMessage>
						Your learning pathway is being prepared. Check back soon!
					</Styled.StateMessage>
				)}

				{mentor.recommendation && (
					<DrSecDimRecommendation
						recommendation={mentor.recommendation}
						selectedCount={selectedCount}
						disabled={isRegenerateBusy}
						canRegenerate={canRegenerate}
						onRegenerateControlClick={onRegenerateControlClick}
					/>
				)}
			</Styled.PanelBody>
		</Styled.ContentWrapper>
	)
}

export default DrSecDimCard
